import React, { useEffect, useState } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import Lightbox from '../components/Lightbox'

export default function ServiceDetail() {
  const { slug } = useParams()
  const navigate = useNavigate()
  const [pageData, setPageData] = useState(null)
  const [galleryData, setGalleryData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [lightboxIndex, setLightboxIndex] = useState(null)

  // Map slugs to default images if CMS doesn't have them
  const defaultImages = {
    'tagarbejde': 'https://assets.auxodev.dk/assets/37f7f16d-7c9c-4f81-b10c-2a08ddb4d637/imgi-10-nyt-tegltag-2-3-min-1024x768-640x480-c-212.png',
    'renovering': 'https://assets.auxodev.dk/assets/37f7f16d-7c9c-4f81-b10c-2a08ddb4d637/imgi-20-sorthus-108.png',
    'tilbygning': 'https://assets.auxodev.dk/assets/37f7f16d-7c9c-4f81-b10c-2a08ddb4d637/imgi-7-tilbygning-4-4-min-1024x768-640x480-c-248.png',
    'nybyg': 'https://assets.auxodev.dk/assets/37f7f16d-7c9c-4f81-b10c-2a08ddb4d637/imgi-11-pavilion-min-1024x768-640x480-c-45.png',
    'nyt-gulv': 'https://assets.auxodev.dk/assets/37f7f16d-7c9c-4f81-b10c-2a08ddb4d637/imgi-12-sillebenparket-2-min-1024x768-640x480-c-49.png',
    'vinduer-og-doere': 'https://assets.auxodev.dk/assets/37f7f16d-7c9c-4f81-b10c-2a08ddb4d637/imgi-7-nye-vinduer-og-doere-min-1024x768-640x480-c-112.png',
  }

  const serviceImage = defaultImages[slug] || 'https://assets.auxodev.dk/assets/37f7f16d-7c9c-4f81-b10c-2a08ddb4d637/imgi-4-bannerimg-1.png'

  useEffect(() => {
    setLoading(true)
    setPageData(null)
    setGalleryData(null)

    // Fetch pages
    const fetchPage = fetch('https://api.dev.auxo.dk/public/content/pages')
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data)) {
          const matchedPage = data.find((p) => p.page_slug === slug)
          if (matchedPage) {
            setPageData(matchedPage)
          } else {
            // If no matched page, redirect to 404
            navigate('/404')
          }
        }
      })

    // Fetch galleries
    const fetchGallery = fetch('https://api.dev.auxo.dk/public/content/galleries')
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data)) {
          const matchedGallery = data.find((g) => g.gallery_slug === slug)
          if (matchedGallery) {
            setGalleryData(matchedGallery)
          }
        }
      })

    Promise.all([fetchPage, fetchGallery])
      .catch((err) => console.error('Error fetching service data', err))
      .finally(() => setLoading(false))
  }, [slug, navigate])

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <i className="fa-solid fa-spinner animate-spin text-red-700 text-4xl" />
          <p className="text-xs font-bold uppercase tracking-widest text-zinc-500">Henter indhold...</p>
        </div>
      </div>
    )
  }

  if (!pageData) return null

  const title = pageData.meta_title || `${pageData.title} - Hansen og Mortensen`
  const description = pageData.meta_description || `Professionelt tømrerarbejde inden for ${slug} i Fredensborg og omegn.`

  // Parse HTML content to render beautifully
  // We can clean up the raw HTML from CMS to match our premium styling
  const cleanContent = pageData.content
    .replace(/<h1>.*?<\/h1>/gi, '') // Remove h1 since we render it in the hero
    .replace(/<p>Har du brug for hjælp til denne type opgave\?.*?<\/p>/gi, '') // Remove intro paragraph
    .replace(/<p>Lad os stå for din næste opgave\..*?<\/p>/gi, '') // Remove intro paragraph
    .replace(/<h3>Har du en forespørgsel\?<\/h3>.*?<\/p>/gi, '') // Remove the duplicate inquiry block at the bottom of CMS content
    .replace(/<h4>​Kontakt os i dag for tagarbejde i København<\/h4>.*?<\/p>/gi, '') // Remove duplicate contact block
    .replace(/<h4>​Kontakt os i dag for tagarbejde i København<\/h4>/gi, '')

  return (
    <div className="flex flex-col min-h-screen">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:image" content={serviceImage} />
        <link rel="canonical" href={window.location.href} />
      </Helmet>

      {/* Hero Banner */}
      <section className="relative h-[40vh] min-h-[300px] w-full bg-neutral-950 flex items-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={serviceImage}
            alt={pageData.title}
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-neutral-950 to-neutral-950/40" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <span className="text-red-500 text-xs font-extrabold uppercase tracking-widest mb-2 block">
            Ydelser
          </span>
          <h1 className="text-2xl sm:text-5xl font-black text-white uppercase tracking-tight">
            {slug === 'vinduer-og-doere' ? 'Vinduer og døre' : slug === 'nyt-gulv' ? 'Nyt gulv' : pageData.title.split('|')[0].trim()}
          </h1>
          <div className="flex items-center gap-2 text-xs text-zinc-400 uppercase tracking-wider mt-4">
            <Link to="/" className="hover:text-white transition-colors">Forside</Link>
            <span className="text-zinc-600">/</span>
            <span className="text-zinc-400">Ydelser</span>
            <span className="text-zinc-600">/</span>
            <span className="text-white">{slug === 'vinduer-og-doere' ? 'Vinduer og døre' : slug === 'nyt-gulv' ? 'Nyt gulv' : pageData.title.split('|')[0].trim()}</span>
          </div>
        </div>
      </section>

      {/* Main Content Section */}
      <section className="pt-0 pb-20 lg:py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            {/* Left: Content */}
            <div className="lg:col-span-7 flex flex-col gap-6">
              {/* Render CMS Rich Text Content */}
              <div
                className="prose prose-zinc max-w-none text-zinc-600 text-sm sm:text-base leading-relaxed space-y-6 
                  prose-headings:text-neutral-950 prose-headings:font-black prose-headings:uppercase prose-headings:tracking-tight prose-headings:mt-8 prose-headings:mb-4
                  prose-h2:text-xl prose-h2:sm:text-2xl
                  prose-h3:text-lg prose-h3:sm:text-xl
                  prose-strong:text-neutral-950 prose-strong:font-bold
                  prose-ul:list-disc prose-ul:pl-6 prose-ul:space-y-2
                  prose-ol:list-decimal prose-ol:pl-6 prose-ol:space-y-2"
                dangerouslySetInnerHTML={{ __html: cleanContent }}
              />
            </div>

            {/* Right: Image & Quick Contact */}
            <div className="lg:col-span-5 relative lg:sticky lg:top-8 flex flex-col gap-8">
              <div className="relative">
                <div className="absolute -left-4 -top-4 w-12 h-12 border-l-4 border-t-4 border-red-700 hidden sm:block" />
                <div className="absolute -right-4 -bottom-4 w-12 h-12 border-r-4 border-b-4 border-neutral-950 hidden sm:block" />
                <img
                  src={serviceImage}
                  alt={pageData.title}
                  className="w-full h-[350px] object-cover shadow-xl border-4 border-neutral-950"
                />
              </div>

              {/* Quick Info Box */}
              <div className="bg-zinc-50 p-6 border border-zinc-200">
                <h3 className="text-sm font-extrabold uppercase tracking-wider text-neutral-950 mb-4">
                  Hvorfor vælge os?
                </h3>
                <ul className="space-y-3 text-xs sm:text-sm text-zinc-600">
                  <li className="flex items-start gap-2">
                    <i className="fa-solid fa-check text-red-700 mt-0.5" />
                    <span>Over 25 års erfaring inden for tømrerfaget.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="fa-solid fa-check text-red-700 mt-0.5" />
                    <span>Tæt dialog og løbende opdateringer under hele processen.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="fa-solid fa-check text-red-700 mt-0.5" />
                    <span>Højeste kvalitet i materialer og udførelse.</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Dynamic Gallery Section */}
      {galleryData && galleryData.images && galleryData.images.length > 0 && (
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-zinc-50 border-y border-zinc-200">
          <div className="max-w-7xl mx-auto">
            <div className="text-center flex flex-col items-center mb-16">
              <span className="text-red-700 text-xs font-extrabold uppercase tracking-widest mb-3">
                Galleri
              </span>
              <h2 className="text-2xl sm:text-4xl font-black text-neutral-950 uppercase tracking-tight">
                Vores arbejde i billeder
              </h2>
              <div className="w-16 h-1 bg-red-700 mt-4" />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {galleryData.images.map((imgUrl, idx) => (
                <div
                  key={idx}
                  onClick={() => setLightboxIndex(idx)}
                  className="relative h-64 overflow-hidden border border-zinc-200 group cursor-pointer"
                >
                  <img
                    src={imgUrl.includes('assets.auxodev.dk') ? imgUrl.replace('assets.auxodev.dk/assets', 'assets.auxodev.dk/cdn-cgi/image/width=600,quality=80,format=auto/assets') : imgUrl}
                    alt={`Galleri ${idx + 1}`}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-neutral-950/0 group-hover:bg-neutral-950/40 transition-all duration-300 flex items-center justify-center">
                    <i className="fa-solid fa-magnifying-glass-plus text-white text-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Lightbox */}
      {lightboxIndex !== null && galleryData && (
        <Lightbox
          images={galleryData.images}
          initialIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
        />
      )}
    </div>
  )
}
